import React, { useState } from "react";
import { Fieldset, Field, Label, ErrorMessage } from "../../design/fieldset";
import { Input } from "../../design/input";
import { Button } from "../../design/button";
import { EmploymentInfo } from "../../models/EmploymentInfo";
import { ParticipantService } from "../../services/ParticipantService";
import { Select } from "../../design/select";

interface Errors {
  employer?: string;
  occupation?: string;
  grossPayFrequency?: string;
}

interface Props {
  participantId: string;
  value?: EmploymentInfo;
  onFormSubmitted?: () => void;
}

const EmploymentForm: React.FC<Props> = ({
  participantId,
  value,
  onFormSubmitted,
}) => {
  const [formState, setFormState] = useState<EmploymentInfo>(value ?? {});
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    await ParticipantService.updateEmployment(participantId, formState);
    setErrors({});
    onFormSubmitted?.();
  };

  return (
    <form onSubmit={handleSubmit}>
      <Fieldset className="space-y-4" disabled={isSaving}>
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
          <Field>
            <Label htmlFor="employer">Employer</Label>
            <Input
              id="employer"
              value={formState.employer || ""}
              placeholder="e.g. ABC Corp"
              onChange={(e) =>
                setFormState({ ...formState, employer: e.target.value })
              }
              disabled={isSaving}
            />
            {errors.employer && <ErrorMessage>{errors.employer}</ErrorMessage>}
          </Field>

          <Field>
            <Label htmlFor="occupation">Occupation</Label>
            <Input
              id="occupation"
              value={formState.occupation || ""}
              placeholder="e.g. Software Engineer"
              onChange={(e) =>
                setFormState({ ...formState, occupation: e.target.value })
              }
              disabled={isSaving}
            />
            {errors.occupation && (
              <ErrorMessage>{errors.occupation}</ErrorMessage>
            )}
          </Field>

          <Field>
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              value={formState.phone || ""}
              placeholder="e.g. (123) 456-7890"
              onChange={(e) =>
                setFormState({ ...formState, phone: e.target.value })
              }
              disabled={isSaving}
            />
          </Field>

          <Field>
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              value={formState.address || ""}
              placeholder="e.g. 123 Main St, City, State"
              onChange={(e) =>
                setFormState({ ...formState, address: e.target.value })
              }
              disabled={isSaving}
            />
          </Field>

          <Field>
            <Label htmlFor="startDate">Start Date</Label>
            <Input
              id="startDate"
              type="date"
              value={
                formState.startDate instanceof Date
                  ? formState.startDate.toISOString().split("T")[0]
                  : formState.startDate || ""
              }
              onChange={(e) =>
                setFormState({
                  ...formState,
                  startDate: new Date(e.target.value),
                })
              }
              disabled={isSaving}
            />
          </Field>

          <Field>
            <Label htmlFor="endDate">End Date</Label>
            <Input
              id="endDate"
              type="date"
              value={
                formState.endDate instanceof Date
                  ? formState.endDate.toISOString().split("T")[0]
                  : formState.endDate || ""
              }
              onChange={(e) =>
                setFormState({
                  ...formState,
                  endDate: new Date(e.target.value),
                })
              }
              disabled={isSaving}
            />
          </Field>

          <Field>
            <Label htmlFor="hoursPerWeek">Hours Per Week</Label>
            <Input
              id="hoursPerWeek"
              type="number"
              placeholder="e.g. 40"
              value={formState.hoursPerWeek ?? ""}
              onChange={(e) =>
                setFormState({
                  ...formState,
                  hoursPerWeek: Number(e.target.value),
                })
              }
              disabled={isSaving}
            />
          </Field>

          <Field>
            <Label htmlFor="grossPay">Gross Pay</Label>
            <Input
              id="grossPay"
              type="number"
              value={formState.grossPay ?? ""}
              placeholder="e.g. 50000"
              onChange={(e) =>
                setFormState({
                  ...formState,
                  grossPay: Number(e.target.value),
                })
              }
              disabled={isSaving}
            />
          </Field>

          <Field>
            <Label htmlFor="grossPayFrequency">Pay Frequency</Label>
            <Select
              id="grossPayFrequency"
              className="input"
              value={formState.grossPayFrequency || ""}
              onChange={(e) =>
                setFormState({
                  ...formState,
                  grossPayFrequency: e.target.value as
                    | "hourly"
                    | "weekly"
                    | "monthly",
                })
              }
              disabled={isSaving}
            >
              <option value="">Select</option>
              <option value="hourly">Hourly</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </Select>
            {errors.grossPayFrequency && (
              <ErrorMessage>{errors.grossPayFrequency}</ErrorMessage>
            )}
          </Field>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end pt-4">
          <Button type="submit" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Employment"}
          </Button>
        </div>
      </Fieldset>
    </form>
  );
};

export default EmploymentForm;
